import axios from 'axios';
import { getRouterBase } from './basePath';
import { buildLoginUrlWithRedirect } from './redirectUtils';

// Store navigation function and logout function to be set by the app
let navigateFunction: ((path: string) => void) | null = null;
let logoutFunction: (() => void) | null = null;
let refreshTokenFunction: (() => Promise<void>) | null = null;

export const setNavigateFunction = (navigate: (path: string) => void) => {
  navigateFunction = navigate;
};

export const setLogoutFunction = (logout: () => void) => {
  logoutFunction = logout;
};

const setRefreshTokenFunction = (refreshToken: () => Promise<void>) => {
  refreshTokenFunction = refreshToken;
};

function getApiBaseUrl() {
  const base = import.meta.env.VITE_API_BASE_URL || '';
  
  // In development, use /api for Vite proxy
  if (import.meta.env.DEV) {
    return '/api';
  }
  
  // If we have a full URL (starts with http), use it as-is
  if (base.startsWith('http')) {
    const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
    return cleanBase;
  }
  
  // For relative paths or empty base, use /api (Vite proxy will handle it)
  return '/api';
}

const api = axios.create({
  baseURL: getApiBaseUrl(),
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    
    // Don't try to refresh tokens for auth requests - those are authentication failures, not expired tokens
    const isAuthRequest = originalRequest.url?.includes('/auth/login') || 
                         originalRequest.url?.includes('/auth/signup') ||
                         originalRequest.url?.includes('/auth/forgot-password') ||
                         originalRequest.url?.includes('/auth/refresh') ||
                         originalRequest.url?.includes('/auth/update-password-action') ||
                         originalRequest.url?.includes('/auth/get-profile-for-update') ||
                         originalRequest.url?.includes('/auth/update-profile-action');
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        if (refreshTokenFunction) {
          await refreshTokenFunction();
          const newToken = localStorage.getItem('token');
          
          if (newToken) {
            processQueue(null, newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        }
        
        throw new Error('Token refresh failed');
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // If refresh fails, logout and redirect
        if (window.location.pathname !== '/login') {
          // Clear authentication data
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          delete api.defaults.headers.common['Authorization'];
          
          // Call logout function if available to clear auth state
          if (logoutFunction) {
            logoutFunction();
          }
          
          const errorMessage = error.response?.data?.detail || 'Your session has expired. Please sign in again to continue.';
          sessionStorage.setItem('loginError', errorMessage);
          
          // Build login URL with current path as redirectTo query param
          const currentPath = window.location.pathname + window.location.search + (window.location.hash || '');
          const loginUrl = buildLoginUrlWithRedirect(currentPath);
          
          // Use React Router navigation if available, otherwise fallback to window.location
          if (navigateFunction) {
            navigateFunction(loginUrl);
          } else {
            window.location.href = loginUrl;
          }
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // Handle other errors (403, etc.)
    // Skip redirect for auth requests — those pages display 403 errors inline
    // (e.g. turnstile failure, account locked, required profile update)
    if (error.response?.status === 403 && !isAuthRequest) {
      if (window.location.pathname !== '/login') {
        const errorMessage = error.response?.data?.detail || 'Access denied';
        sessionStorage.setItem('loginError', errorMessage);
        
        if (navigateFunction) {
          navigateFunction('/access-forbidden');
        } else {
          window.location.href = '/access-forbidden';
        }
      }
    }

    if (error.response?.status === 404) {
      if (window.location.pathname !== '/login') {
        const errorMessage = error.response?.data?.detail || 'Page not found';
        sessionStorage.setItem('loginError', errorMessage);
        if (navigateFunction) {
          navigateFunction('/404');
        } else {
          window.location.href = '/404';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Utility function for authenticated fetch requests (for streaming/SSE that can't use axios)
export const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('token');
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  // Handle 401 Unauthorized - redirect to login
  if (response.status === 401) {
    console.warn('🔐 [authenticatedFetch] Authentication failed, redirecting to login');
    
    // Clear authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete api.defaults.headers.common['Authorization'];
    
    // Call logout function if available to clear auth state
    if (logoutFunction) {
      logoutFunction();
    }
    
    const errorMessage = 'Your session has expired. Please sign in again to continue.';
    sessionStorage.setItem('loginError', errorMessage);
    
    // Build login URL with current path as redirectTo query param
    const currentPath = window.location.pathname + window.location.search + (window.location.hash || '');
    const loginUrl = buildLoginUrlWithRedirect(currentPath);
    
    // Use React Router navigation if available, otherwise fallback to window.location
    if (navigateFunction) {
      navigateFunction(loginUrl);
    } else {
      window.location.href = loginUrl;
    }
    
    throw new Error('Authentication failed');
  }
  
  return response;
};

export default api;
export { getApiBaseUrl, setRefreshTokenFunction }; 